import { makeAutoObservable, reaction } from "mobx";
import { BillingItem, CompanyProductToBillingItemMapper } from "services/checkoutBasket/checkoutBasketTypes";
import { type CompanyStore } from "services/company/companyStore";
import { type CompanyProductsStore } from "services/companyProducts/companyProductsStore";
import { CompanyProduct } from "services/companyProducts/companyProductsTypes";
import type { PricingPlanStore } from "services/pricingPlan/au/pricingPlanStore";

const mapper = new CompanyProductToBillingItemMapper({
  [CompanyProduct.VirtualOffice]: BillingItem.VirtualOffice,
});

export class CheckoutBasketStore {
  private _addedItems: BillingItem[] = [];
  private _additionalItems: BillingItem[] = [];
  private _selectedPlanProducts: BillingItem[] = [];

  constructor(
    private readonly _companyStore: CompanyStore,
    private readonly _companyProductsStore: CompanyProductsStore,
    private readonly _pricingPlanStore: PricingPlanStore,
  ) {
    this._distributeItems();

    makeAutoObservable(this);

    reaction(
      () => [this._companyStore.company?.pricingPlan, this._companyProductsStore.productsData],
      this._distributeItems.bind(this),
    );
  }

  get addedItems(): BillingItem[] {
    return this._addedItems;
  }

  set addedItems(value: BillingItem[]) {
    this._addedItems = value;
  }

  get additionalItems(): BillingItem[] {
    return this._additionalItems;
  }

  set additionalItems(value: BillingItem[]) {
    this._additionalItems = value;
  }

  get selectedPlanProducts(): BillingItem[] {
    return this._selectedPlanProducts;
  }
  set selectedPlanProducts(value: BillingItem[]) {
    this._selectedPlanProducts = value;
  }

  private _distributeItems(): void {
    type ItemDistributionResult = { addedItems: BillingItem[]; additionalItems: BillingItem[] };
    const itemDistributionFiltered = mapper.billingItems.filter((billingItem) => {
      if (billingItem === BillingItem.FormationExpress) {
        // Formation Express card has it‘s own place
        return false;
      }
      return !this._pricingPlanStore.isProductIncluded(mapper.getProduct(billingItem));
    });
    const itemDistribution = itemDistributionFiltered.reduce<ItemDistributionResult>(
      (result, billingItem) => {
        switch (billingItem) {
          case BillingItem.VirtualOffice:
            if (this._companyProductsStore.hasProduct(CompanyProduct.VirtualOffice)) {
              result.addedItems.push(billingItem);
            } else {
              result.additionalItems.push(billingItem);
            }
            break;
        }

        return result;
      },
      { addedItems: [], additionalItems: [] },
    );

    const excludedPricingPlanProducts = mapper.billingItems
      .filter((billingItem) => this._pricingPlanStore.isProductIncluded(mapper.getProduct(billingItem)))
      .map((billingItem) => mapper.getProduct(billingItem));
    const selectedProducts = this._companyProductsStore.productsData.map((product) => product.product);

    const selectedPlanProducts = excludedPricingPlanProducts
      .filter((product) => selectedProducts.includes(product))
      .map((product) => mapper.getBillingName(product));

    this.selectedPlanProducts = selectedPlanProducts;

    this.addedItems = itemDistribution.addedItems;
    this.additionalItems = itemDistribution.additionalItems;
  }
}
